<template>
  <div class="my-devices">
    <!-- 查询条件区域 -->
    <div class="query-section">
      <div class="query-form">
        <div class="form-item">
          <label class="form-label">MAD：</label>
          <el-input
            v-model="queryParams.mad"
            placeholder="请输入"
            clearable
            class="input-field"
          />
        </div>
        <div class="form-item">
          <label class="form-label">出厂日期：</label>
          <el-date-picker
            v-model="queryParams.productionDate"
            type="date"
            placeholder="请选择日期"
            value-format="YYYY-MM-DD"
            class="input-field"
          />
        </div>
        <div class="button-group">
          <el-button class="reset-btn" @click="handleReset">重置</el-button>
          <el-button type="primary" class="query-btn" @click="handleQuery"
            >查询</el-button
          >
        </div>
      </div>
    </div>

    <!-- 数据展示区域 -->
    <div class="data-section">
      <div class="section-header">
        <div>
          <span style="font-size: 14px; color: #333">设备管理</span>
        </div>
        <div class="toolbar">
          <!-- 刷新按钮 -->
          <el-button type="text" @click="refreshData">
            <el-icon><Refresh /></el-icon>
          </el-button>
          
          <!-- 密度设置 -->
          <el-popover
            placement="bottom"
            width="160"
            v-model:visible="densityDialogVisible"
          >
            <div class="popover-content">
              <el-button
                class="density-button"
                :type="rowHeight === '80px' ? 'primary' : ''"
                @click="setRowHeight('80px')"
              >
                默认
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '60px' ? 'primary' : ''"
                @click="setRowHeight('60px')"
              >
                中等
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '30px' ? 'primary' : ''"
                @click="setRowHeight('30px')"
              >
                紧凑
              </el-button>
            </div>
            <template #reference>
              <el-button type="text">
                <el-icon><Grid /></el-icon>
              </el-button>
            </template>
          </el-popover>

          <!-- 列显示设置 -->
          <el-popover
            placement="bottom"
            width="200"
            v-model:visible="columnsDialogVisible"
          >
            <div class="popover-content">
              <el-checkbox-group v-model="tempSelectedColumns">
                <el-checkbox
                  v-for="column in columns"
                  :key="column.prop"
                  :label="column.prop"
                >
                  {{ column.label }}
                </el-checkbox>
              </el-checkbox-group>
              <div class="popover-footer">
                <el-button @click="resetColumns">重置</el-button>
                <el-button type="primary" @click="confirmColumns">确定</el-button>
              </div>
            </div>
            <template #reference>
              <el-button type="text">
                <el-icon><Setting /></el-icon>
              </el-button>
            </template>
          </el-popover>
        </div>
      </div>

      <!-- 数据表格 -->
      <el-table
        :data="tableData"
        :row-class-name="tableRowClassName"
        :row-style="getRowStyle"
        :header-cell-style="{
          background: '#f5f7fa',
          color: '#909399',
          textAlign: 'center',
        }"
        :cell-style="{ textAlign: 'center' }"
        style="width: 100%"
      >
        <el-table-column
          v-for="column in visibleColumns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :filters="column.filters"
          :filter-method="column.filterMethod"
          :filter-placement="column.filterPlacement"
          :align="column.align || 'center'"
        >
          <template #header="scope">
            {{ scope.column.label }}
            <i
              v-if="column.filters"
              class="el-icon-filter"
              style="cursor: pointer; color: #409eff"
              @click.stop="showFilter(column.prop)"
            ></i>
          </template>
          <template #default="scope">
            <template v-if="column.prop === 'connectionStatus'">
              <span>
                <i :class="`status-dot ${getStatusClass(scope.row[column.prop])}`"></i>
                {{ scope.row[column.prop] }}
              </span>
            </template>
            <template
              v-else-if="
                column.prop === 'bindUserStatus' || column.prop === 'shareUserStatus'
              "
            >
              <span>
                <i :class="`status-dot ${getStatusClass(scope.row[column.prop])}`"></i>
                <template v-if="column.prop === 'shareUserStatus' && scope.row[column.prop] === 1">
                  已分享<span style="color: #409EFF">{{ scope.row.shareUsers.length }}</span>用户
                </template>
                <template v-else>
                  {{ getStatusText(scope.row[column.prop], column.prop,) }}
                </template>
              </span>
            </template>
            <template v-else-if="column.prop === 'operation'">
              <div class="operation-buttons">
                <el-button
                  type="text"
                  style="color: red"
                  @click="resetDevice(scope.row.mad)"
                >
                  重置设置
                </el-button>
                <el-button
                  type="text"
                  style="color: blue"
                  @click="refreshDevice(scope.row.mad)"
                >
                  刷新
                </el-button>
                <el-button type="text" style="color: blue" @click="editDevice(scope.row)">
                  编辑
                </el-button>
                <el-button
                  type="danger"
                  size="small"
                  @click="handleDeleteDevice(scope.row)"
                  style="margin-top: 3px;"
                >
                  删除
                </el-button>
              </div>
            </template>
            <template v-else-if="column.prop === 'deviceSyncTime'">
              {{ formatDateTime(scope.row[column.prop]) }}
            </template>
            <template v-else>
              {{ scope.row[column.prop] }}
            </template>
          </template>
        </el-table-column>

        <template #empty>
          <div class="empty-state">
            <el-icon class="empty-icon"><Box /></el-icon>
            <p class="empty-text">暂无数据</p>
          </div>
        </template>
      </el-table>
    </div>
    
    <!-- 删除设备确认对话框 -->
    <el-dialog
      v-model="deleteDialogVisible"
      title="删除设备"
      width="30%"
    >
      <span>确定要删除该设备吗？此操作不可逆。</span>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">取消</el-button>
          <el-button type="danger" @click="confirmDeleteDevice">确定</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 设备编辑弹窗 -->
    <el-dialog
      title="设备编辑"
      v-model="editDialogVisible"
      width="50%"
      @close="closeEditDialog"
      class="custom-dialog"
    >
      <div class="edit-dialog-content">
        <div class="left-ui">
          <img
            src="@/assets/bg_item_device.png"
            alt="设备图片"
            style="width: 100%; height: auto"
          />
          <div class="info-section">
            <div class="info-title">基本信息</div>
            <el-divider style="margin: 5px 0px;"/>
            <div class="info-item">
              <span class="label">设备名称：</span>
              <span class="value">{{ editDeviceInfo.name }}</span>
            </div>
            <div class="info-item">
              <span class="label">型号：</span>
              <span class="value">{{ editDeviceInfo.model }}</span>
            </div>
            <div class="info-item">
              <span class="label">MAD：</span>
              <span class="value">{{ editDeviceInfo.mad }}</span>
            </div>
            <div class="info-item">
              <span class="label">出厂批次：</span>
              <span class="value">{{ editDeviceInfo.batch }}</span>
            </div>
            <div class="info-item">
              <span class="label">电击棒次数：</span>
              <span class="value">{{ editDeviceInfo.electricShockCount }}</span>
            </div>
            <div class="info-item">
              <span class="label">熔接总次数：</span>
              <span class="value">{{ editDeviceInfo.fusionCount }}</span>
            </div>
          </div>
        </div>

        <div class="right-ui">
          <div class="user-section">
            <div class="section-title">已绑定用户</div>
            <el-divider style="margin: 5px 0px;"/>
            <div class="user-item">
              <span class="user-email">{{ editDeviceInfo.bindUser || "-" }}</span>
              <el-button
                v-if="editDeviceInfo.bindUser"
                type="primary"
                @click="unbindUser(editDeviceInfo.mad)"
              >
                解除绑定
              </el-button>
            </div>
          </div>

          <div class="share-section">
            <div class="section-title">已分享<span style="color: #409EFF">{{ editDeviceInfo.shareUsers.length }}</span>用户</div>
            <el-divider style="margin: 5px 0px;"/>
            <div
              v-for="(email, index) in editDeviceInfo.shareUsers"
              :key="index"
              class="user-item"
            >
              <span class="user-email">{{ email || "-" }}</span>
              <el-button
                v-if="email"
                type="primary"
                @click="unshareUser(editDeviceInfo.mad, email)"
              >
                解除分享
              </el-button>
            </div>
          </div>

          <div class="section-title">开机密码</div>
          <el-divider style="margin: 5px 0px;"/>
          <div class="password-item">
            <el-input
              v-model="editDeviceInfo.password"
              placeholder="-"
              disabled
              :readonly="true"
              class="password-input"
            />
            <el-button
              v-if="editDeviceInfo.password"
              type="danger"
              @click="clearPassword(editDeviceInfo.mad)"
              class="clear-button"
            >
              清除
            </el-button>
          </div>

          <div class="status-section">
            <el-button
              :type="editDeviceInfo.disableStatus === 0 ? 'primary' : 'danger'"
              @click="toggleStatus(editDeviceInfo.mad)"
              class="status-button"
            >
              {{ editDeviceInfo.disableStatus === 0 ? "禁用" : "解除禁用"  }}
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, h } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
    getDevicesForManagement,
    resetDeviceSettings,
    clearDevicePassword,
    unbindDevice,
    unshareDevice,
    disableDevice,
    enableDevice,
    getDeviceByMad,
    deleteDevice
  } from "@/api/device";
import { Grid, Setting, Refresh, Box } from "@element-plus/icons-vue";

export default {
  name: "MyDevices",
  components: {
    Grid,
    Setting,
    Refresh,
    Box,
  },
  setup() {
    const userRole = ref(1); // 用户角色
    const deleteDialogVisible = ref(false);
    const currentDevice = ref(null);

    // 初始化时获取用户角色并设置可见列
    onMounted(() => {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        userRole.value = user.role; // 0: 管理员, 1: 员工, 2: 维修工程师
        
        // 根据用户角色初始化selectedColumns
        if (userRole.value === 1 || userRole.value === 2) {
          selectedColumns.value = columns.value
            .filter(col => col.prop !== 'operation')
            .map(col => col.prop);
          tempSelectedColumns.value = [...selectedColumns.value];
        }
      }
    });
    const queryParams = ref({
      mad: "",
      productionDate: "",
    });

    const columns = ref([
      { prop: "index", label: "序号", visible: true },
      { prop: "mad", label: "MAD", visible: true },
      { prop: "productionDate", label: "出厂日期", visible: true },
      { prop: "deviceType", label: "设备类型", visible: true },
      { prop: "lastConnectionPosition", label: "位置", visible: true },
      {
        prop: "bindUserStatus",
        label: "是否绑定用户",
        visible: true,
        filters: [
          { text: "已绑定", value: 1 },
          { text: "未绑定", value: 0 },
        ],
        filterMethod: (value, row) => row.bindUserStatus === value,
        filterPlacement: "bottom-end",
      },
      {
        prop: "shareUserStatus",
        label: "是否分享用户",
        visible: true,
        filters: [
          { text: "已分享", value: 1 },
          { text: "未分享", value: 0 },
        ],
        filterMethod: (value, row) => row.shareUserStatus === value,
        filterPlacement: "bottom-end",
      },
      { prop: "deviceSyncTime", label: "设备同步时间", visible: true },
      { prop: "operation", label: "操作", visible: true },
    ]);

    const selectedColumns = ref(columns.value.map((col) => col.prop));
    const tempSelectedColumns = ref([...selectedColumns.value]);

    const visibleColumns = computed(() =>
      columns.value.filter((col) => selectedColumns.value.includes(col.prop))
    );

    const rowHeight = ref("80px");
    const densityDialogVisible = ref(false);
    const columnsDialogVisible = ref(false);

    const tableData = ref([]);

    // 获取设备列表数据
    const fetchData = async (isShow) => {
      try {
        const response = await getDevicesForManagement(queryParams.value);
        if (response.data && response.success) {
          tableData.value = response.data;
          if(isShow)
            ElMessage.success("数据已刷新");
        } else {
          ElMessage.error(response.message || "获取数据失败");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        ElMessage.error("获取数据失败");
      }
    };

    // // 初始化时获取数据
    // onMounted(() => {
    //   fetchData();
    // });

    // 刷新数据
    const refreshData = () => {
      fetchData(true);
    };

    // 查询数据
    const handleQuery = () => {
      fetchData(true);
    };

    // 重置查询条件
    const handleReset = () => {
      queryParams.value.mad = "";
      queryParams.value.productionDate = "";
      fetchData(true);
    };

    const getStatusClass = (status) => {
      return status === 1 ? "status-dot-green" : "status-dot-red";
    };

    const getStatusText = (status, prop, length) => {
      if (prop === 'bindUserStatus') {
        return status === 1 ? "已绑定" : "未绑定";
      } else if (prop === 'shareUserStatus') {
        if (status === 1) {
          return h('span', {}, [
            '分享',
            h('span', { style: { color: '#409EFF' } }, length),
            '用户'
          ]);
        }
        return "未分享";
      }
      return status === 1 ? "已绑定" : "未绑定";
    };

    const getRowStyle = () => ({
      height: rowHeight.value,
      transition: "height 0.3s ease",
    });

    const tableRowClassName = ({ rowIndex }) =>
      rowIndex % 2 === 0 ? "even-row" : "odd-row";

    const setRowHeight = (height) => {
      rowHeight.value = height;
      densityDialogVisible.value = false;
    };

    const resetColumns = () => {
      // 根据用户角色决定是否显示操作列
      if (userRole.value !== 0) {
        // 非管理员角色不显示操作列
        tempSelectedColumns.value = columns.value
          .filter(col => col.prop !== 'operation')
          .map(col => col.prop);
      } else {
        // 管理员显示所有列
        tempSelectedColumns.value = columns.value.map(col => col.prop);
      }
    };

    const confirmColumns = () => {
      if (tempSelectedColumns.value.length === 0) {
        ElMessage.warning("至少选择一列");
        return;
      }
      
      // 非管理员角色强制移除操作列
      if (userRole.value !== 0) {
        const index = tempSelectedColumns.value.indexOf('operation');
        if (index > -1) {
          tempSelectedColumns.value.splice(index, 1);
        }
      }
      
      selectedColumns.value = [...tempSelectedColumns.value];
      columnsDialogVisible.value = false;
    };

      // 重置设备设置
      const resetDevice = async (mad) => {
        try {
          await ElMessageBox.confirm("确认重置该设备？确定后会恢复出厂设置", "提示", {
          type: "warning",
        });
          await resetDeviceSettings(mad);
          refreshDevice(mad);
          ElMessage.success("设备重置成功");
        } catch (error) {
          ElMessage.error("设备重置失败");
        }
      };

    const refreshDevice = async (mad) => {
      try {
        // 从服务器重新获取该设备数据
        const res = await getDeviceByMad(mad);
        ElMessage.success("设备刷新成功");
        const device = res.data;
        const index = tableData.value.findIndex((item) => item.mad === mad);
        if (index !== -1) {
          tableData.value[index] = device;
        }
      } catch (error) {
        ElMessage.error("设备刷新失败");
      }
    };

    const editDialogVisible = ref(false);
    const editDeviceInfo = ref({});

    
    // 处理删除设备
    const handleDeleteDevice = (row) => {
      currentDevice.value = row;
      deleteDialogVisible.value = true;
    };
    
    // 确认删除设备
    const confirmDeleteDevice = async () => {
      try {
        await deleteDevice(currentDevice.value.mad);
        ElMessage.success('设备删除成功');
        deleteDialogVisible.value = false;
        await fetchData(true); // 重新加载数据
      } catch (error) {
        console.error('删除设备失败:', error);
        ElMessage.error('删除设备失败');
      }
    };

    const openEditDialog = (device) => {
      // 假设从服务器获取设备详细信息
      editDeviceInfo.value = {
        name: device.deviceName,
        model: device.deviceType,
        mad: device.mad,
        batch: device.productionDate,
        bindUser: device.bindUser,
        shareUsers: device.shareUsers || [],
        password: device.password,
        disableStatus: device.status,
        electricShockCount: device.electricShockCount,
        fusionCount: device.fusionCount,
        lastConnectionPosition: device.lastConnectionPosition,
      };
      editDialogVisible.value = true;
    };

    const closeEditDialog = () => {
      editDialogVisible.value = false;
      refreshData()
    };

    const unbindUser = async (mad) => {
      try {
        await ElMessageBox.confirm("确认解绑该设备？所有分享用户将一起删除", "提示", {
          type: "warning",
        });
        // 向服务器发送解除绑定请求
        await unbindDevice(mad);
        ElMessage.success("解除绑定成功");
        editDeviceInfo.value.bindUser = null;
        editDeviceInfo.value.shareUsers = [];
      } catch (error) {
        ElMessage.error("解除绑定失败");
      }
    };

    const unshareUser = async (mad, email) => {
      try {
        // 向服务器发送解除分享请求
        await unshareDevice(mad, email);
        ElMessage.success("解除分享成功");
        editDeviceInfo.value.shareUsers = editDeviceInfo.value.shareUsers.filter(
          (userEmail) => userEmail !== email
        );
      } catch (error) {
        ElMessage.error("解除分享失败");
      }
    };

    const clearPassword = async (mad) => {
      try {
        // 向服务器发送清除密码请求
        await clearDevicePassword(mad);
        ElMessage.success("清除密码成功");
        editDeviceInfo.value.password = "";
      } catch (error) {
        ElMessage.error("清除密码失败");
      }
    };

    const toggleStatus = async (mad) => {
      try {
        // 向服务器发送状态切换请求
        if(editDeviceInfo.value.disableStatus === 1){
          await enableDevice(mad);
        }else{
          await disableDevice(mad);
        }
        ElMessage.success("状态切换成功");
        editDeviceInfo.value.disableStatus =
          editDeviceInfo.value.disableStatus === 1 ? 0 : 1;
      } catch (error) {
        ElMessage.error("状态切换失败");
      }
    };

    const editDevice = (row) => {
      openEditDialog(row); // 确保传递当前行数据
    };

    onMounted(() => fetchData(false));

        // 格式化日期时间
        const formatDateTime = (dateTimeStr) => {
      if (!dateTimeStr) return '-';
      // 处理ISO格式的时间字符串，如2025-03-10T22:16:23.064057
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) return dateTimeStr; // 如果日期无效，返回原字符串
      
      // 格式化为 年-月-日 时:分:秒
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    return {
      queryParams,
      columns,
      visibleColumns,
      tableData,
      tempSelectedColumns,
      selectedColumns,
      rowHeight,
      densityDialogVisible,
      columnsDialogVisible,
      deleteDialogVisible,
      getStatusClass,
      getStatusText,
      getRowStyle,
      tableRowClassName,
      setRowHeight,
      resetColumns,
      confirmColumns,
      refreshData,
      handleQuery,
      handleReset,
      resetDevice,
      refreshDevice,
      editDevice,
      editDialogVisible,
      editDeviceInfo,
      unbindUser,
      unshareUser,
      clearPassword,
      toggleStatus,
      formatDateTime,
      closeEditDialog,
      handleDeleteDevice,
      confirmDeleteDevice,
    };
  },
};
</script>

<style scoped>
.my-devices {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.query-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 10px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.data-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.query-form {
  display: flex;
  align-items: center;
  gap: 30px;
}

.form-item {
  display: flex;
  align-items: center;
}

.form-label {
  width: 80px;
  font-size: 14px;
  color: #606266;
}

.input-field {
  width: 220px;
}

.button-group {
  margin-left: auto;
  display: flex;
  gap: 15px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
}

.toolbar {
  display: flex;
  gap: 8px;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}

.status-dot-green {
  background-color: #67c23a;
}

.status-dot-red {
  background-color: #f56c6c;
}

.popover-content {
  display: flex;
  flex-direction: column;
}

.popover-footer {
  margin-top: 10px;
  text-align: right;
}

.density-button {
  display: flex;
  justify-content: center;
  margin: 10px;
}

.el-table {
  width: 100% !important;
  margin: 0;
}

.even-row {
  background-color: #fafafa;
}

.odd-row {
  background-color: #ffffff;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  color: #909399;
  margin-bottom: 16px;
}

.empty-text {
  color: #909399;
  margin: 0;
  font-size: 14px;
}

.edit-dialog-content {
  display: flex;
  justify-content: space-between;
}

.left-ui,
.right-ui {
  width: 48%;
}

.info-section,
.user-section,
.share-section,
.password-section,
.status-section {
  margin-bottom: 20px;
}

.info-title,
.section-title {
  font-size: 16px;
  font-weight: bold;
}

.info-item,
.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.label {
  font-weight: bold;
}

.user-email {
  flex: 1;
}

.user-item .el-button,
.password-item .el-button {
  margin-left: 10px;
}
.operation-buttons {
  display: flex;
}
.custom-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 1000 !important;
  overflow: auto !important;
  max-height: 80vh !important;
}
.password-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.password-input {
  flex: 1;
  background-color: #f5f7fa;
  color: #909399;
}

.clear-button {
  margin-left: 10px;
}

.status-section {
  width: 100%;
}

.status-button {
  width: 100%;
}
</style>
